import { MaybeRefOrGetter, toValue } from "vue";
import type { TokenInputActions } from "@/src/design_system/form_elements/token_input/composables/useTokenInputActions";
import {
  DS_TOKEN_INPUT_BACKSPACE,
  DS_TOKEN_INPUT_COMMIT_REASON,
  DS_TOKEN_INPUT_EDIT_MODE,
  DSInputToken,
  DSTokenInputBackspace,
  DSTokenInputEditMode,
} from "@/src/design_system/form_elements/token_input/types";

interface UseTokenInputKeyboardOptions {
  actions: TokenInputActions;
  tokens: MaybeRefOrGetter<DSInputToken[]>;
  draft: MaybeRefOrGetter<string | undefined>;
  backspace: MaybeRefOrGetter<DSTokenInputBackspace | undefined>;
  commitKeys: MaybeRefOrGetter<string[] | undefined>;
  isInactive: MaybeRefOrGetter<boolean>;
  commit: TokenInputActions["commitDraft"];
}

interface UseTokenInputKeyboard {
  onKeydown: (event: KeyboardEvent) => void;
}

type KeyHandler = (textarea: HTMLTextAreaElement) => boolean;
type SelectionHandler = (index: number) => boolean;

const DEFAULT_COMMIT_KEYS = ["Enter"];

const hasModifier = (event: KeyboardEvent): boolean => event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;

const isTypedCharacter = (event: KeyboardEvent): boolean =>
  event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey;

const isCollapsed = (textarea: HTMLTextAreaElement): boolean => textarea.selectionStart === textarea.selectionEnd;

const isAtStart = (textarea: HTMLTextAreaElement): boolean => isCollapsed(textarea) && textarea.selectionStart === 0;

const isAtEnd = (textarea: HTMLTextAreaElement): boolean =>
  isCollapsed(textarea) && textarea.selectionEnd === textarea.value.length;

export const useTokenInputKeyboard = ({
  actions,
  tokens,
  draft,
  backspace,
  commitKeys,
  isInactive,
  commit,
}: UseTokenInputKeyboardOptions): UseTokenInputKeyboard => {
  const { caret, selectedIndex, select, moveTo, remove, edit } = actions;

  const isDraftEmpty = (): boolean => !toValue(draft);
  const tokenCount = (): number => toValue(tokens).length;
  const isCommitKey = (key: string): boolean => (toValue(commitKeys) ?? DEFAULT_COMMIT_KEYS).includes(key);
  const editModeOf = (index: number): DSTokenInputEditMode =>
    toValue(tokens)[index]?.editMode ?? DS_TOKEN_INPUT_EDIT_MODE.Text;
  const isTextToken = (index: number): boolean => editModeOf(index) === DS_TOKEN_INPUT_EDIT_MODE.Text;

  const moveCaretTo = (index: number): boolean => {
    if (index < 0 || index > tokenCount()) {
      return false;
    }

    select(null);
    if (index !== caret.value) {
      moveTo(index);
    }

    return true;
  };

  const leaveDraft = (offset: number, key: string): boolean => {
    if (isDraftEmpty()) {
      return moveCaretTo(caret.value + offset);
    }

    commit(DS_TOKEN_INPUT_COMMIT_REASON.Move, key);

    return true;
  };

  const editOrSelect = (index: number): void => (isTextToken(index) ? edit(index) : select(index));

  const backspaceActions: Record<DSTokenInputBackspace, (index: number) => void> = {
    [DS_TOKEN_INPUT_BACKSPACE.Select]: select,
    [DS_TOKEN_INPUT_BACKSPACE.Edit]: editOrSelect,
    [DS_TOKEN_INPUT_BACKSPACE.Remove]: remove,
  };

  const actOnToken = (index: number): boolean => {
    if (!isDraftEmpty() || index < 0 || index >= tokenCount()) {
      return false;
    }

    backspaceActions[toValue(backspace) ?? DS_TOKEN_INPUT_BACKSPACE.Select](index);

    return true;
  };

  const keyHandlers: Record<string, KeyHandler> = {
    ArrowLeft: textarea => isAtStart(textarea) && leaveDraft(-1, "ArrowLeft"),
    ArrowRight: textarea => isAtEnd(textarea) && leaveDraft(1, "ArrowRight"),
    Home: () => isDraftEmpty() && moveCaretTo(0),
    End: () => isDraftEmpty() && moveCaretTo(tokenCount()),
    Backspace: () => actOnToken(caret.value - 1),
    Delete: () => actOnToken(caret.value),
  };

  const editSelected: SelectionHandler = index => {
    if (!isTextToken(index)) {
      return false;
    }

    edit(index);

    return true;
  };

  const removeSelected: SelectionHandler = index => {
    remove(index);

    return true;
  };

  const selectionHandlers: Record<string, SelectionHandler> = {
    Backspace: removeSelected,
    Delete: removeSelected,
    Enter: editSelected,
    F2: editSelected,
    Escape: () => {
      select(null);

      return true;
    },
  };

  const typeOnSelected = (index: number): void => {
    if (editModeOf(index) === DS_TOKEN_INPUT_EDIT_MODE.Replace) {
      remove(index);
    } else {
      select(null);
    }
  };

  const handleSelection = (event: KeyboardEvent): boolean => {
    const index = selectedIndex.value;
    const handler = selectionHandlers[event.key];

    if (index === null) {
      return false;
    }
    if (handler && !hasModifier(event)) {
      return handler(index);
    }
    if (isTypedCharacter(event) && !isCommitKey(event.key)) {
      typeOnSelected(index);
    }

    return false;
  };

  const handleCommitKey = (key: string): boolean => {
    if (!isCommitKey(key)) {
      return false;
    }
    if (!isDraftEmpty()) {
      commit(DS_TOKEN_INPUT_COMMIT_REASON.Key, key);
    }

    return true;
  };

  const handleKey = (event: KeyboardEvent): boolean => {
    if (isTypedCharacter(event) && handleCommitKey(event.key)) {
      return true;
    }
    if (hasModifier(event)) {
      return false;
    }

    return handleCommitKey(event.key) || (keyHandlers[event.key]?.(event.target as HTMLTextAreaElement) ?? false);
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (toValue(isInactive) || event.isComposing) {
      return;
    }
    if (handleSelection(event) || handleKey(event)) {
      event.preventDefault();
    }
  };

  return { onKeydown };
};
